import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabaseClient';
import { authService } from '@/services/authService';
import { UserProfile, UserRole, UserPermissions, ADMIN_FULL_PERMISSIONS, OPERATIONS_PERMISSIONS, VIEWER_PERMISSIONS } from '@/types/auth';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  role: UserRole | null;
  isAdmin: boolean;
  isOperations: boolean;
  isViewer: boolean;
  loading: boolean;
  error: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  hasPermission: (module: keyof UserPermissions, action?: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProfile = async (userId: string) => {
    try {
      const userProfile = await authService.getProfile(userId);
      setProfile(userProfile);
    } catch (err: any) {
      console.error('[AuthContext] Failed to load user profile:', err);
      setError(err.message || 'Failed to load profile');
    }
  };

  useEffect(() => {
    let mounted = true;

    // Check initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mounted) return;
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id).finally(() => {
          if (mounted) setLoading(false);
        });
      } else {
        setLoading(false);
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, newSession) => {
      if (!mounted) return;
      setSession(newSession);
      setUser(newSession?.user ?? null);
      if (newSession?.user) {
        await fetchProfile(newSession.user.id);
      } else {
        setProfile(null);
      }
      setLoading(false);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    setError(null);
    setLoading(true);
    try {
      const data = await authService.signIn(email, password);
      setUser(data.user);
      setSession(data.session);
      if (data.user) {
        await fetchProfile(data.user.id);
      }
    } catch (err: any) {
      setError(err.message || 'Invalid email or password');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    setLoading(true);
    try {
      await authService.signOut();
      setUser(null);
      setSession(null);
      setProfile(null);
    } catch (err: any) {
      console.error('[AuthContext] Sign out error:', err);
    } finally {
      setLoading(false);
    }
  };

  const refreshProfile = async () => {
    if (user) {
      await fetchProfile(user.id);
    }
  };

  const role = profile?.role || null;
  const isAdmin = role === 'admin';
  const isOperations = role === 'admin' || role === 'operations';
  const isViewer = role === 'admin' || role === 'operations' || role === 'viewer';

  const hasPermission = (module: keyof UserPermissions, action: string = 'view'): boolean => {
    // Unrestricted admin access (Cez / Admin)
    if (isAdmin) return true;

    // Check custom JSONB permissions on profile
    const modulePerms = profile?.permissions?.[module];
    if (modulePerms && typeof modulePerms === 'object') {
      return Boolean((modulePerms as any)[action]);
    }

    // Default fallback based on role
    const fallbackMatrix =
      role === 'operations'
        ? OPERATIONS_PERMISSIONS
        : role === 'viewer'
        ? VIEWER_PERMISSIONS
        : ADMIN_FULL_PERMISSIONS;

    const rolePerms = fallbackMatrix[module];
    if (rolePerms && typeof rolePerms === 'object') {
      return Boolean((rolePerms as any)[action]);
    }

    return false;
  };

  const value = useMemo(
    () => ({
      user,
      session,
      profile,
      role,
      isAdmin,
      isOperations,
      isViewer,
      loading,
      error,
      signIn,
      signOut,
      refreshProfile,
      hasPermission,
    }),
    [user, session, profile, role, isAdmin, isOperations, isViewer, loading, error]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
